<template>
  <b-nav-item-dropdown
    v-if="canUserWorkDayComponent"
    class="dropdown-employee-work-day mr-25"
    menu-class="dropdown-menu-media mt-50 w-auto p-2"
    title="Ваш рабочий день"
    right
  >
    <template #button-content>
      <h5 class="mb-0">
        <b-badge
          pill
          :variant="workDay ? 'light-info' : 'light-primary'"
          class="d-flex align-items-center justify-content-center"
        >
          <feather-icon :icon="workDayIcon"></feather-icon>
          <span class="ml-50 time mr-50">{{ currentTime }}</span>
          <feather-icon icon="ClockIcon"></feather-icon>
        </b-badge>
      </h5>
    </template>
    <li class="dropdown-menu-header mb-1">
      <div class="d-flex flex-column align-items-center mb-1">
        <div class="d-flex align-items-center justify-content-between w-100 mb-1">
          <div class="d-flex align-items-center">
            <feather-icon
              badge-classes="bg-danger"
              class="text-body mr-1"
              icon="ClockIcon"
              size="21"
            ></feather-icon>
            <p class="text-center mb-0">Ваш рабочий день{{ dayIsFinished ? ' завершен' : '' }}</p>
          </div>
          <div
            v-if="workDayTime"
            class="font-weight-bold time"
          >{{ workDayTime }}</div>
        </div>
        <div class="day-controls d-flex w-100">
          <template v-for="btn in buttons">
            <b-button
              v-if="btn.visible"
              class="d-flex align-items-center"
              :variant="btn.variant"
              :title="`${btn.label} рабочий день`"
              @click="onClickBtn(btn)"
            >
              <feather-icon
                :icon="btn.icon"
                size="21"
                class="mr-50"
              ></feather-icon>
              {{ btn.label }}
            </b-button>
          </template>
        </div>
        <div
          v-if="pauseIsActive"
          class="d-flex w-100 font-weight-bold mt-1 align-items-center"
        >
          <span class="mr-50">Перерыв:</span>
          <span>{{ pauseTime }}</span>
        </div>
      </div>
    </li>
    <li class="dropdown-item d-flex flex-column align-items-center p-0">
      <b-form-group
        label="Сделано за день"
        label-for="report"
        class="w-100"
      >
        <b-form-textarea
          v-model="report"
          id="report"
          placeholder="Напишите краткий отчет о проделанной работе"
          rows="5"
          trim
        ></b-form-textarea>
      </b-form-group>
      <b-button
        class="mt-50 d-flex align-items-center"
        variant="outline-success"
        @click="saveReport"
      >
        <feather-icon
          icon="SaveIcon"
          size="21"
          class="mr-50"
        ></feather-icon>
        Сохранить
      </b-button>
    </li>
  </b-nav-item-dropdown>
</template>

<script>
import {
  BNavItemDropdown, BDropdownItem, BBadge, BFormTextarea, BButton, BFormGroup,
} from 'bootstrap-vue'
import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import moment from 'moment/moment'
import store from '@/store'
import { entityEmployeeWorkDay } from '@/libs/acl/entities'
import { showFailedOperationToast, showSuccessOperationToast } from '@/composables/toast'
import ability from '@/libs/acl/ability'

export default {
  name: 'EmployeeWorkDay',
  components: {
    BFormGroup, BButton, BFormTextarea, BBadge, BNavItemDropdown, BDropdownItem
  },
  setup() {
    const timeInterval = ref(null)
    const pauseInterval = ref(null)
    const currentTime = ref(moment().format('HH:mm'))
    const buttons = ref([
      {type: 'start', label: 'Начать', icon: 'PlayIcon', variant: 'success', visible: false},
      {type: 'pause', label: 'Приостановить', icon: 'PauseIcon', variant: 'secondary', visible: false},
      {type: 'continue', label: 'Продолжить', icon: 'PlayIcon', variant: 'success', visible: false},
      {type: 'resume', label: 'Возобновить', icon: 'RefreshCcwIcon', variant: 'info', visible: false},
      {type: 'finish', label: 'Завершить', icon: 'StopCircleIcon', variant: 'danger', visible: false},
    ])
    const canUserWorkDayComponent = computed(() => ability.can('create', entityEmployeeWorkDay))
    const pauseIsActive = computed(() => workDay.value && workDay.value.status === 'pause')
    const dayIsFinished = computed(() => workDay.value && workDay.value.status === 'finish')
    const workDayIcon = computed(() => {
      let icon = 'PlayIcon'
      if (workDay.value) {
        if (workDay.value.status === 'active') {
          icon = 'PauseIcon'
        } else if (workDay.value.status === 'finish') {
          icon = 'RefreshCcwIcon'
        }
      }
      return icon
    })
    const workDayTimestamp = ref(null)
    const workDayTime = ref(null)
    const workDay = ref(null)
    const pauseTime = ref(null)
    const pauseTimeTimestamp = ref(null)
    const report = ref(null)

    const onClickBtn = async btn => {
      switch (btn.type) {
        case 'start':
          await store.dispatch('schedule/startEmployeeWorkDay')
            .then(({ data }) => {
              workDay.value = data
              processActiveDay()
              processControls()
            })
            .catch(() => showFailedOperationToast('Не удалось начать рабочий день'))
          break
        case 'pause':
          await store.dispatch('schedule/updateEmployeeWorkDay', {id: workDay.value.id, status: btn.type})
            .then(({ data }) => {
              workDay.value = data
              processPause()
              processControls()
            })
            .catch(() => showFailedOperationToast('Не удалось изменить рабочий день'))
          break
        case 'continue':
        case 'resume':
          await store.dispatch('schedule/updateEmployeeWorkDay', {id: workDay.value.id, status: btn.type})
            .then(({ data }) => {
              workDay.value = data
              processControls()
            })
            .catch(() => showFailedOperationToast('Не удалось изменить рабочий день'))
          break
        case 'finish':
          await store.dispatch('schedule/updateEmployeeWorkDay', {id: workDay.value.id, status: btn.type})
            .then(({ data }) => {
              workDay.value = data
              setCurrentWorkDayTime()
              processControls()
            })
            .catch(() => showFailedOperationToast('Не удалось изменить рабочий день'))
          break
      }
    }
    const getCurrentWorkDay = async () => await store.dispatch('schedule/getEmployeeCurrentWorkDay')
      .then(({ data }) => {
        workDay.value = data ? data : null
        report.value = data && data.report ? data.report : null
      })
    const processControls = () => {
      buttons.value.map(btn => {
        if (btn.type === 'start') {
          btn.visible = !workDay.value
        }
        if (btn.type === 'pause') {
          btn.visible = workDay.value && workDay.value.status === 'active'
        }
        if (btn.type === 'continue') {
          btn.visible = workDay.value && workDay.value.status === 'pause'
        }
        if (btn.type === 'finish') {
          btn.visible = workDay.value && ['pause', 'active'].includes(workDay.value.status)
        }
        if (btn.type === 'resume') {
          btn.visible = workDay.value && workDay.value.status === 'finish'
        }
      })
    }
    const processPause = () => {
      if (!pauseTimeTimestamp.value) {
        const duration = moment.duration(moment().diff(moment(parseInt(workDay.value.last_pause_time))))
        pauseTimeTimestamp.value = duration.asMilliseconds()
        if (!workDay.value.last_pause_time && workDay.value.pause_duration) {
          pauseTimeTimestamp.value += workDay.value.pause_duration * 1000
        }
      }
      pauseTimeTimestamp.value += 1000
      pauseTime.value = moment(pauseTimeTimestamp.value).utc().format('HH:mm:ss')
      let duration = moment.duration(
        moment(parseInt(workDay.value.last_pause_time)).diff(moment(parseInt(workDay.value.start_time)))
      )
      duration = duration.asMilliseconds()
      if (workDay.value.pause_duration) {
        duration -= workDay.value.pause_duration * 1000
      }
      workDayTimestamp.value = duration
      workDayTime.value = moment(workDayTimestamp.value).utc().format('HH:mm:ss')
    }
    const processActiveDay = () => {
      if (!workDayTimestamp.value) {
        const duration = moment.duration(moment().diff(moment(parseInt(workDay.value.start_time))))
        workDayTimestamp.value = duration.asMilliseconds()
      }
      workDayTimestamp.value += 1000
      workDayTime.value = moment(workDayTimestamp.value).utc().format('HH:mm:ss')
    }
    const setCurrentWorkDayTime = () => {
      let timeFrom = workDay.value.last_pause_time ? workDay.value.last_pause_time : workDay.value.finish_time
      let duration = moment.duration(moment(parseInt(timeFrom)).diff(moment(parseInt(workDay.value.start_time))))
      duration = duration.asMilliseconds()
      if (workDay.value.pause_duration) {
        duration -= workDay.value.pause_duration * 1000
      }
      workDayTimestamp.value = duration
      workDayTime.value = moment(workDayTimestamp.value).utc().format('HH:mm:ss')
    }
    const saveReport = async () => {
      await store.dispatch('schedule/updateEmployeeWorkDay', {id: workDay.value.id, report: report.value})
        .then(({ data }) => {
          workDay.value = data
          showSuccessOperationToast('Отчет о рабочем дне успешно сохранен')
        })
        .catch(() => showFailedOperationToast('Не удалось сохранить отчет о рабочем дне'))
    }

    onBeforeMount(async () => {
      if (canUserWorkDayComponent.value) {
        timeInterval.value = setInterval(() => {
          currentTime.value = moment().format('HH:mm')
          if (workDay.value && workDay.value.id) {
            if (workDay.value.status === 'pause') {
              processPause()
            } else if (workDay.value.status === 'active') {
              processActiveDay()
            } else {
              setCurrentWorkDayTime()
            }
          }
        }, 1000)
        await getCurrentWorkDay()
        processControls()
      }
    })

    onBeforeUnmount(() => {
      if (timeInterval.value) {
        clearInterval(timeInterval.value)
        timeInterval.value = null
        workDayTimestamp.value = null
        workDayTime.value = null
      }

      if (pauseInterval.value) {
        clearInterval(pauseInterval.value)
        pauseInterval.value = null
        pauseTime.value = null
        pauseTimeTimestamp.value = null
      }
    })

    return {
      dayIsFinished,
      pauseIsActive,
      buttons,
      currentTime,
      workDay,
      workDayIcon,
      workDayTime,
      pauseTime,
      report,
      canUserWorkDayComponent,

      onClickBtn,
      saveReport
    }
  }
}
</script>

<style lang='scss'>

@import '@/assets/scss/style';

.dropdown-employee-work-day {
  .dropdown-menu-header {
    .day-controls {
      .btn:not(:first-child) {
        margin-left: 1rem;
      }
    }
    .time {
      width: 52px;
    }
  }

  .badge svg {
    width: 21px;
    height: 21px;
  }

  .dropdown-item {
    &:focus, &:hover {
      background-color: unset;
    }
  }
}

.dark-layout {
  .dropdown-menu-header {
    color: $theme-dark-body-color;
    border-bottom: 1px solid $theme-dark-border-color !important;
  }
}

@media (min-width: 1009px) {
  .dropdown-employee-work-day {
    .dropdown-menu {
      width: 500px !important;
    }
  }
}

@media (min-width: 768px) and (max-width: 1008px) {
  .dropdown-employee-work-day {
    .dropdown-menu {
      width: 300px !important;
    }
  }
}

@media (max-width: 1008px) {
  .dropdown-employee-work-day {
    .dropdown-menu {
      overflow-y: scroll !important;
      max-height: 300px;

      &::-webkit-scrollbar {
        width: 5px;
        height: 5px;
      }

      &::-webkit-scrollbar-thumb {
        background: $gray-500;
        border-radius: 20px;
      }

      &::-webkit-scrollbar-track {
        background: $gray-300;
        border-radius: 30px;
      }

      .dropdown-menu-header {
        .day-controls {
          flex-direction: column;

          .btn:not(:first-child) {
            margin-left: 0;
            margin-top: .5rem;
          }
        }
      }
    }
  }
}

@media (max-width: 397px) {
  .dropdown-employee-work-day {
    .time, .feather-clock {
      display: none;
    }
    .feather-play {
      padding-left: 2px;
    }
  }
}

</style>
